import { ApolloError, gql, useMutation } from '@apollo/client'
import { useCallback } from 'react'

export const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword($input: ChangePasswordInput!) {
    changePassword(input: $input) {
      user {
        email
      }
    }
  }
`

type ChangePasswordMutation = {
  changePassword: { user: { email: string | null } | null } | null
}

type ChangePasswordMutationVariables = {
  input: {
    oldPassword: string
    newPassword: string
  }
}

export type ChangePasswordErrorKind = 'incorrectCurrent' | 'generic'

export type ChangePasswordResult =
  | { success: true }
  | { success: false; kind: ChangePasswordErrorKind }

const INCORRECT_CURRENT_PASSWORD_KEY = 'usuario.senha_antiga_incorreta'

function readErrorKey(gqlError: unknown): string | undefined {
  const candidate = gqlError as {
    info?: { key?: string }
    extensions?: { info?: { key?: string } }
  }
  return candidate?.info?.key ?? candidate?.extensions?.info?.key
}

function extractErrorKind(error: unknown): ChangePasswordErrorKind {
  if (error instanceof ApolloError) {
    const hasIncorrectCurrent = error.graphQLErrors.some(
      (gqlError) => readErrorKey(gqlError) === INCORRECT_CURRENT_PASSWORD_KEY
    )
    if (hasIncorrectCurrent) return 'incorrectCurrent'
  }
  return 'generic'
}

export function useChangePassword() {
  const [mutate, { loading }] = useMutation<
    ChangePasswordMutation,
    ChangePasswordMutationVariables
  >(CHANGE_PASSWORD_MUTATION)

  const execute = useCallback(
    async (input: ChangePasswordMutationVariables['input']): Promise<ChangePasswordResult> => {
      try {
        await mutate({ variables: { input } })
        return { success: true }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production')
          console.error('[useChangePassword] mutation failed', err)

        return { success: false, kind: extractErrorKind(err) }
      }
    },
    [mutate]
  )

  return { execute, loading }
}
